import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaPlus } from 'react-icons/fa';
import { FaHouse, FaRightFromBracket, FaLayerGroup, FaUsers, FaUserPlus } from 'react-icons/fa6';
import { useAppContext } from '../context/AppContext';
import './SideNavBar.css';

const SideNavBar = ({ isMobileOpen = false, onClose = () => {}, onOpenTutorial = () => {} }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAppContext();
  const [hoveredButton, setHoveredButton] = useState(null);

  // Check if user is admin
  const isAdmin = user?.role && user.role.toLowerCase() === 'admin';

  const buttons = [
    {
      id: 'home',
      icon: FaHouse,
      label: 'Library',
      path: '/home',
      tooltip: 'Library',
    },
    {
      id: 'context-variables',
      icon: FaLayerGroup,
      label: 'Context Variables',
      path: '/context-variables',
      tooltip: 'Context Variables',
    },
    // Admin-only navigation items
    ...(isAdmin ? [
      {
        id: 'admin-roles',
        icon: FaUsers,
        label: 'Roles & Responsibilities',
        path: '/admin/roles',
        tooltip: 'Roles & Responsibilities',
      },
      {
        id: 'admin-users',
        icon: FaUserPlus,
        label: 'Add User',
        path: '/admin/users',
        tooltip: 'Add User (no email verification)',
      },
    ] : []),
  ];

  const isActive = (path) => {
    if (path === '/home') {
      return location.pathname === '/home';
    }
    if (path === '/context-variables') {
      return location.pathname === '/context-variables';
    }
    if (path === '/admin/roles') {
      return location.pathname.startsWith('/admin/roles');
    }
    if (path === '/admin/users') {
      return location.pathname === '/admin/users';
    }
    return location.pathname.startsWith(path);
  };

  const handleAddPrompt = () => {
    navigate('/add-prompt');
    onClose();
  };

  const handleNavClick = (path) => {
    navigate(path);
    onClose();
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    onClose();
  };

  return (
    <nav className={`side-nav-bar ${isMobileOpen ? 'open' : ''}`} aria-label="Primary navigation">
      {buttons.map((button) => {
        const Icon = button.icon;
        const active = button.path ? isActive(button.path) : false;
        return (
          <div key={button.id} className="nav-button-wrapper">
            <button
              className={`nav-button ${active ? 'active' : ''}`}
              onClick={button.onClick || (() => handleNavClick(button.path))}
              onMouseEnter={() => setHoveredButton(button.id)}
              onMouseLeave={() => setHoveredButton(null)}
              aria-label={button.tooltip}
            >
              <Icon className="nav-icon" />
              <span className="nav-label">{button.label}</span>
            </button>
            {hoveredButton === button.id && (
              <div className="tooltip">{button.tooltip}</div>
            )}
          </div>
        );
      })}
      <div className="nav-button-wrapper">
        <button
          className="nav-button"
          onClick={handleLogout}
          onMouseEnter={() => setHoveredButton('logout')}
          onMouseLeave={() => setHoveredButton(null)}
          aria-label="Logout"
        >
          <FaRightFromBracket className="nav-icon" />
          <span className="nav-label">Logout</span>
        </button>
        {hoveredButton === 'logout' && (
          <div className="tooltip">Logout</div>
        )}
      </div>
      <div className="nav-button-wrapper">
        <button
          className="nav-button add-button"
          onClick={handleAddPrompt}
          onMouseEnter={() => setHoveredButton('add')}
          onMouseLeave={() => setHoveredButton(null)}
          aria-label="Add Prompt"
        >
          <FaPlus className="nav-icon" />
          <span className="nav-label">Add Prompt</span>
        </button>
        {hoveredButton === 'add' && (
          <div className="tooltip">Add Prompt</div>
        )}
      </div>
      <div className="legal-links-section">
        <button
          className="legal-link-button"
          onClick={() => handleNavClick('/terms-of-service')}
          aria-label="Terms of Service"
        >
          <span className="legal-link-text">Terms of Service</span>
        </button>
        <button
          className="legal-link-button"
          onClick={() => handleNavClick('/privacy-policy')}
          aria-label="Privacy Policy"
        >
          <span className="legal-link-text">Privacy Policy</span>
        </button>
      </div>
    </nav>
  );
};

export default SideNavBar;
